import { useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_OPS_ERROR_LOGS, OPS_INCIDENT, OPS_INCIDENTS } from '../queries/opsIncidents';
import { CREATE_OPS_INCIDENT, RUN_LOG_MONITOR, UPDATE_OPS_INCIDENT } from '../mutations/opsIncidents';

const NONE = [];

/**
 * The ops incident tickets, filtered. `status: ''` means every status — sent
 * as such, because the API's own default for a missing status is "open".
 * Empty severity, priority and service are sent as null: no filter.
 */
export function useOpsIncidents({ status = 'open', severity = '', priority = '', service = '', limit = 200 } = {}) {
  const { data, loading, error, refetch } = useQuery(OPS_INCIDENTS, {
    variables: {
      status,
      severity: severity || null,
      priority: priority || null,
      service: service || null,
      limit,
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => ({
    rows: data?.opsIncidents ?? NONE,
    loading,
    error,
    refetch,
  }), [data, loading, error, refetch]);
}

/** One ticket, whole; skipped with no id. */
export function useOpsIncident(id) {
  const { data, loading, error, refetch } = useQuery(OPS_INCIDENT, {
    variables: { id: Number(id) },
    skip: id == null,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });
  return { incident: data?.opsIncident ?? null, loading, error, refetch };
}

/**
 * One page of stored error lines. Empty filters are sent as null: no filter.
 * `cursor` is the previous page's `nextCursor`; null for the first page.
 */
export function useOpsErrorLogs({
  from = '', to = '', level = '', service = '', exceptionType = '', fingerprint = '', cursor = null, limit = 100,
} = {}) {
  const { data, loading, error, refetch } = useQuery(GET_OPS_ERROR_LOGS, {
    variables: {
      from: from || null,
      to: to || null,
      level: level || null,
      service: service || null,
      exceptionType: exceptionType || null,
      fingerprint: fingerprint || null,
      cursor: cursor || null,
      limit,
    },
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  return useMemo(() => ({
    rows: data?.getOpsErrorLogs?.items ?? NONE,
    nextCursor: data?.getOpsErrorLogs?.nextCursor ?? null,
    loading,
    error,
    refetch,
  }), [data, loading, error, refetch]);
}

const REFRESH = { refetchQueries: ['OpsIncidents', 'OpsIncident'] };
export const useCreateOpsIncident = () => useMutation(CREATE_OPS_INCIDENT, REFRESH);
export const useUpdateOpsIncident = () => useMutation(UPDATE_OPS_INCIDENT, REFRESH);
/* A real run raises and updates tickets and stores error lines, so both lists
   are read again after it. */
export const useRunLogMonitor = () => useMutation(RUN_LOG_MONITOR, { refetchQueries: ['OpsIncidents', 'GetOpsErrorLogs'] });
